import React from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { FONT, R, labelSx, primaryButtonSx } from "./roomTheme";

/*
 * Bidding panel pieces (compact). They only display values and call the handlers they are
 * given; amounts, rules (disabled / highest bidder) and handlers all come from the pages.
 */

const SIZES = {
  current: { label: "0.75rem", value: { xs: "1.375rem", md: "1.5rem", xl: "1.625rem" } }, // 12px / 22–26px
  next: { label: "0.6875rem", value: { xs: "1.0625rem", md: "1.125rem", xl: "1.25rem" } }, // 11px / 17–20px
  small: { label: "0.625rem", value: { xs: "0.9375rem", md: "1rem" } },
};

/** Label + amount. size: "current" | "next" | "small". */
export function PriceStat({ label, value, note, size = "next", color, sx }) {
  const s = SIZES[size] || SIZES.next;
  return (
    <Box sx={{ minWidth: 0, ...sx }}>
      <Typography sx={{ ...labelSx, fontSize: s.label }}>{label}</Typography>
      <Typography
        sx={{
          fontFamily: FONT,
          fontWeight: 700,
          fontSize: s.value,
          lineHeight: 1.15,
          color: color || R.ink,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </Typography>
      {note && (
        <Typography sx={{ ...labelSx, fontSize: "0.625rem", fontWeight: 400, mt: 0.25, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {note}
        </Typography>
      )}
    </Box>
  );
}

/** Place Bid button (desktop), 52px. `winning` switches to the "highest bidder" look. */
export function PlaceBidButton({ disabled, winning, primary, secondary, onClick }) {
  return (
    <Button
      disabled={disabled}
      onClick={onClick}
      fullWidth
      disableElevation
      variant="contained"
      sx={{
        ...primaryButtonSx,
        minHeight: 52,
        py: 0.5,
        flexDirection: "column",
        lineHeight: 1.15,
        bgcolor: winning ? R.success : R.ink,
        "&:hover": { bgcolor: winning ? "#256B29" : "#333" },
        "&.Mui-disabled": winning ? { bgcolor: R.success, color: "#fff", opacity: 0.85 } : { bgcolor: R.lineStrong, color: R.secondary },
      }}
    >
      <Box component="span" sx={{ fontSize: "1rem", letterSpacing: "1px" }}>
        {primary}
      </Box>
      <Box component="span" sx={{ fontSize: "0.625rem", letterSpacing: "2px", opacity: 0.9 }}>
        {secondary}
      </Box>
    </Button>
  );
}

/** Full-width button for "Login to bid" / "Join room request" (48px). */
export function AccessButton({ children, onClick }) {
  return (
    <Button onClick={onClick} fullWidth variant="contained" disableElevation sx={{ ...primaryButtonSx, fontSize: "0.8125rem" }}>
      {children}
    </Button>
  );
}

const NOTICE_TEXT = {
  pending: "You are already registered in this auction. Please wait for approval or call Leon Gallery.",
  declined:
    "You have declined to participate in this auction. For further assistance, please contact Leon Gallery Staff or call (02)8856-2781",
};

/** Registration notices (pending / declined) — same messages as before. */
export function AccessNotice({ kind }) {
  return (
    <Alert
      icon={<PriorityHighIcon fontSize="inherit" />}
      severity="warning"
      sx={{
        borderRadius: 0,
        border: "1px solid #E7C66A",
        bgcolor: R.warningBg,
        color: "#5C4A00",
        fontFamily: FONT,
        fontSize: "0.8125rem",
        lineHeight: 1.4,
        py: 0.25,
        px: 1.5,
        alignItems: "flex-start",
        "& .MuiAlert-icon": { color: "#8A6D00", py: 0.75, mr: 1 },
        "& .MuiAlert-message": { py: 0.75 },
      }}
    >
      {NOTICE_TEXT[kind]}
    </Alert>
  );
}
